import { useContext, useMemo } from 'react';
import { Tooltip } from '@usealloy/component-library';

import { getDollarCurrencyRightAttributeValueMode } from 'flow/editor/components/thresholds/helpers';
import type { ThresholdResult } from 'flow/store/engine/types/workflowFeatures';

import { ContainerType } from '../../../../types';
import { RuleBuilderContext } from '../../../context';
import { ThresholdContext } from '../context';
import {
  StyledGreenDot,
  StyledOutputBadge,
  StyledOutputBadgeContainer,
  StyledOutputText,
} from './styles';

type ThresholdOutputValueProps = {
  thresholdResult: ThresholdResult;
};

const ThresholdOutputValue = ({
  thresholdResult,
}: ThresholdOutputValueProps) => {
  const { containerType } = useContext(RuleBuilderContext);
  const { populatedThreshold } = useContext(ThresholdContext);

  const dollarCurrencyRightAttributeValueMode = useMemo(() => (
    getDollarCurrencyRightAttributeValueMode(populatedThreshold.left)
  ), [populatedThreshold.left]);

  const outputValue = thresholdResult.is_tag_set !== null
    ? `${thresholdResult.is_tag_set ? 'Set' : 'Not Set'}`
    : `${thresholdResult.left_value ?? 'No output value'}`;

  const tooltipContent = thresholdResult.is_true
    ? `Satisfies logic: ${outputValue}`
    : `Does not satisfy logic: ${outputValue}`;

  // Format currency values only for numeric outputs
  const displayValue = useMemo(() => {
    if (dollarCurrencyRightAttributeValueMode) {
      const parsedValue = parseInt(outputValue, 10);
      // Only format as currency if parsing resulted in a valid number
      if (!Number.isNaN(parsedValue)) {
        return `$${(parsedValue / 100).toFixed(2)}`;
      }
    }
    return outputValue;
  }, [dollarCurrencyRightAttributeValueMode, outputValue]);

  return (
    <StyledOutputBadgeContainer>
      <StyledGreenDot isVisible={thresholdResult.is_true} />
      <Tooltip
        content={tooltipContent}
        side="top"
        style={{
          wordBreak: 'break-all',
        }}
      >
        {containerType !== ContainerType.ISLAND && (
          <StyledOutputBadge
            outline={false}
            satisfiesLogic={thresholdResult.is_true}
          >
            <StyledOutputText>
              {displayValue}
            </StyledOutputText>
          </StyledOutputBadge>
        )}
      </Tooltip>
    </StyledOutputBadgeContainer>
  );
};

export default ThresholdOutputValue;
